// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Download from "@insite/mobius/Icons/Download";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import setShowWelcomePopup from "@store/Framework/Store/Store/Components/WelcomePopup/Handlers/SetShowWelcomePopup";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const LogoWidget = getWidgetComponent("Basic/Logo");

const enum fields {
    logoImage = "logoImage",
    closeAndSeeAccountLabel = "closeAndSeeAccountLabel",
    welcomeLetterUrl = "welcomeLetterUrl",
    downloadLetterLabel = "downloadLetterLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.logoImage]: string;
        [fields.closeAndSeeAccountLabel]: string;
        [fields.welcomeLetterUrl]: string;
        [fields.downloadLetterLabel]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

const mapStateToProps = (state: ApplicationState) => {
    const { firstName, lastName, userName } = state.context.session;

    return {
        userDisplayName: firstName ? `${firstName}${lastName ? ` ${lastName}` : ""}` : userName,
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    };
};

const mapDispatchToProps = { setShowWelcomePopup };

export interface WelcomePopupStyles {
    wrapper?: InjectableCss;
    topLetterWrapper?: InjectableCss;
    bgRedLines?: InjectableCss;
    welcomeLogo?: InjectableCss;
    welcomeTitle?: TypographyPresentationProps;
    buttonWrapper?: InjectableCss;
    footerWrapper?: InjectableCss;
    buttonClose?: ButtonPresentationProps;
    buttonDownload?: ButtonPresentationProps;
}

export const welcomePopupStyles: WelcomePopupStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor("secondary")};
        `,
    },
    topLetterWrapper: {
        css: css`
            ${breakpointValues(
                [pxToRem(24), pxToRem(36), pxToRem(56), pxToRem(64), pxToRem(72)],
                ["padding-right", "padding-left"],
            )}
            padding-top: ${pxToRem(40)};
        `,
    },
    bgRedLines: {
        css: css`
            display: block;
            width: 45px;
            height: 237px;
            position: absolute;
            top: 0;
            left: 0;
            background-image: url("staticContent/images/redDiagonalLines.png");
            background-position: top left;
            background-repeat: no-repeat;
            padding-left: 0;
            padding-right: 0;
            pointer-events: none;
        `,
    },
    welcomeLogo: {
        css: css`
            justify-content: center;
        `,
    },
    welcomeTitle: {
        variant: "h1",
        color: "accent",
        transform: "uppercase",
        css: css`
            text-align: center;
            margin: ${pxToRem(40)} 0;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;

            button {
                margin-bottom: ${pxToRem(16)};
            }
        `,
    },
    footerWrapper: {
        css: css`
            background-color: ${getColor("common.background")};
            padding: 1px 0 ${pxToRem(40)} 0;
        `,
    },
    buttonDownload: {
        buttonType: "outline",
        icon: {
            src: Download,
            position: "right",
        },
    },
};
const styles = welcomePopupStyles;

const WelcomePopup = ({ id, userDisplayName, myAccountPageUrl, setShowWelcomePopup, fields }: Props) => {
    const history = useHistory();

    const modalGoToMyAccount = () => {
        setShowWelcomePopup(false);
        if (myAccountPageUrl) {
            if (myAccountPageUrl.startsWith("http")) {
                window.location.href = myAccountPageUrl;
            } else {
                history.push(myAccountPageUrl);
            }
        }
    };
    const modalDownloadHandler = () => {
        window.open(fields.welcomeLetterUrl, "_blank")?.focus();
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.bgRedLines} />
            <StyledWrapper {...styles.topLetterWrapper}>
                <LogoWidget logoImage={fields.logoImage} customCSS={`& {${styles.welcomeLogo?.css}}`} />
                <Typography {...styles.welcomeTitle}>Hello {userDisplayName},</Typography>
            </StyledWrapper>
            <Zone contentId={id} zoneName="LetterContent" />
            <StyledWrapper {...styles.footerWrapper}>
                <Zone contentId={id} zoneName="LetterFooter" />
                <StyledWrapper {...styles.buttonWrapper}>
                    <Button {...styles.buttonClose} onClick={modalGoToMyAccount}>
                        {fields.closeAndSeeAccountLabel}
                    </Button>
                    {fields.welcomeLetterUrl && (
                        <Button {...styles.buttonDownload} onClick={modalDownloadHandler}>
                            {fields.downloadLetterLabel}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(WelcomePopup)),
    definition: {
        group: "Basic",
        allowedContexts: ["WelcomeLetterPage"],
        displayName: "Welcome Letter Body",
        fieldDefinitions: [
            {
                name: fields.logoImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.closeAndSeeAccountLabel,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "Close and See my Account",
            },
            {
                name: fields.welcomeLetterUrl,
                fieldType: "Contextual",
                editorTemplate: "FilePickerField",
                defaultValue: "",
            },
            {
                name: fields.downloadLetterLabel,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "Download Welcome Letter",
            },
        ],
    },
};

export default widgetModule;
